import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../store';
import { IFieldState } from './applicationFormTypes';

export const selectFieldStateByName: (state: RootState) => (fieldName: string) => IFieldState | undefined =
    createSelector(
        (state: RootState) => state,
        (state) =>
            (fieldName: string): IFieldState | undefined =>
                state.form.formFieldStates.find((fieldState) => fieldState.fieldName === fieldName),
    );

export const selectFieldStatesByNames: (state: RootState) => (fieldNames: string[]) => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state) =>
        (fieldNames: string[]): IFieldState[] =>
            state.form.formFieldStates.filter((fieldState) => fieldNames.indexOf(fieldState.fieldName) > -1),
);

export const CONSENT_NAME_PREFIX = 'CONSENT';
export const selectConsentFieldStates: (state: RootState) => () => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state) => (): IFieldState[] =>
        state.form.formFieldStates.filter((fieldState) => fieldState.fieldName.indexOf(CONSENT_NAME_PREFIX) === 0),
);

/**
 * Select all field states
 *
 * @param state the current state
 * @returns a list of all field states
 */
export const selectAllFieldStates: (state: RootState) => () => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state: RootState) => (): IFieldState[] => state.form.formFieldStates,
);

/**
 * Field names tracked in form state for UI/validation purposes only (e.g. gating navigation, toggling
 * another field's visibility) which have no corresponding credit attribute and must never reach the
 * submission payload.
 */
export const UI_ONLY_FIELD_NAMES = ['applicantNewDebtThisYearToggle', 'coapplicantNewDebtThisYearToggle'];

/**
 * Select the field states that should actually be sent in the submit payload: everything except
 * consent-prefixed fields (handled separately via selectConsentFieldStates) and UI_ONLY_FIELD_NAMES.
 *
 * @param state the current state
 * @returns a list of submittable field states
 */
export const selectSubmittableFieldStates: (state: RootState) => () => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state: RootState) => (): IFieldState[] =>
        state.form.formFieldStates.filter(
            (fieldState) =>
                fieldState.fieldName.indexOf(CONSENT_NAME_PREFIX) !== 0 &&
                !UI_ONLY_FIELD_NAMES.includes(fieldState.fieldName),
        ),
);

/**
 * Select the field value given the field name
 *
 * @param state the current state
 * @returns the field value or an empty string if not defined
 */
export const selectFieldValue: (state: RootState) => (fieldName: string) => string | number | boolean | string[] =
    createSelector(
        (state: RootState) => state,
        (state: RootState) =>
            (fieldName: string): string | number | boolean | string[] =>
                state.form.formFieldStates.find((f) => f.fieldName === fieldName)?.value || '',
    );
/**
 * @param the current state
 * @returns a list of all KYC states
 */
export const selectAllKycFieldStates: (state: RootState) => () => IFieldState[] = createSelector(
    (state: RootState) => state,
    (state: RootState) => (): IFieldState[] =>
        state.form.formFieldStates.filter((fieldState) => fieldState.fieldName.startsWith('KYC_')),
);
